import type { EquipmentTreeRowFlat } from "../models";

export interface PartDetailsParams {
  partId: number | null;
  parentPartId: number | null;
  positionId: number | null;
}

/**
 * Determines which params to pass to getPartDetails based on the selected row type.
 *
 * Row type → param mapping:
 * - container: location history mode (parentPartId + positionId=1)
 * - equipment: equipment mode (partId = EquipmentId)
 * - sparePart: part mode (partId = L2PartId)
 * - location: location history mode (parentPartId = EquipmentId, positionId = PositionId)
 *
 * NOTE: For metrics, location rows with an installed part use L2PartId directly.
 * The caller (MetricsPanel in query mode) handles this by using the partId field
 * when it has a value, falling back to parentPartId+positionId for history context.
 */
export const getPartDetailsParams = (
  row: EquipmentTreeRowFlat,
): PartDetailsParams => {
  // Container: use location history mode
  if (row.rowType === "container") {
    return {
      partId: null,
      parentPartId: row.ContainerId ?? null,
      positionId: 1,
    };
  }

  // Equipment: use the equipment ID directly
  if (row.rowType === "equipment") {
    return {
      partId: row.EquipmentId,
      parentPartId: null,
      positionId: null,
    };
  }

  // Spare part: use the part's own ID
  if (row.rowType === "sparePart" && row.L2PartId) {
    return {
      partId: row.L2PartId,
      parentPartId: null,
      positionId: null,
    };
  }

  // Location: provide all three params
  // - Metrics will use partId (L2PartId) when available
  // - History will use parentPartId + positionId for location history mode
  if (row.rowType === "location") {
    return {
      partId: row.L2PartId ?? null,
      parentPartId: row.EquipmentId ?? null,
      positionId: row.PositionId ?? null,
    };
  }

  // Fallback (sparesContainer or unknown)
  return { partId: null, parentPartId: null, positionId: null };
};
